// screen: Monitoring & alerts (ניטור והתראות) — control-plane change monitoring-alerts.
// Monitors (status per check, create wizard, one-click basic channel monitoring, snooze), alert history with ack, and
// destinations (e-mail with double opt-in, Telegram linked through the ViewStream bot). Every call is a documented API
// route; the server scopes everything to the tenant and enforces notifications:manage for changes.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Activity, BellOff, BellRing, CheckCircle2, Mail, MessageCircle, Pause, Pencil, Play, Plus, Send, ShieldCheck, Trash2, Zap } from 'lucide-react';
import type { AlertDestination, ApiError, Monitor, MonitorAlert, MonitorCheck, MonitorCheckDef, MonitorStatus, MonitorTargetKind, ServiceLayer, ServiceLevel } from '@viewstream/api-client';
import { fmtDateTime } from '@viewstream/i18n';
import {
  Badge, Button, Checkbox, ConfirmByNameDialog, Dialog, DialogContent, EmptyState, ErrorState, Field, Input, Select, Sheet, SheetContent, Skeleton, Switch,
  Tabs, TabsContent, TabsList, TabsTrigger, useToast, cn,
} from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { useChannels } from '~/app/live';
import { AIServicesStatus, useAIServiceTexts } from '~/app/AIServices';
import { useLipsyncAvailable } from '~/app/lipsync';
import {
  fmtCheckValue, useAckAlert, useAlerts, useBasicPreset, useCheckCatalog, useCreateDestination, useCreateMonitor, useDeleteDestination, useDeleteMonitor,
  useDestinations, useMonitors, usePatchDestination, usePatchMonitor, useResendDestination, useServiceStatus, useSnooze, useTelegramLink, useTestDestination, useUnsnooze,
} from '~/app/monitoring';

export const Route = createFileRoute('/_layout/t/$slug/monitoring')({ component: Screen });

type Tab = 'monitors' | 'alerts' | 'destinations';

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Monitoring & alerts`); }, [t]);
  const manage = can(scopes, 'notifications:manage');
  const [tab, setTab] = React.useState<Tab>('monitors');
  const firing = useAlerts(tenant.slug, 'firing');
  const openCount = firing.data?.length ?? 0;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t`Monitoring & alerts`}</h1>
        {openCount ? <Badge tone="danger" data-testid="firing-count">{t`${openCount} firing`}</Badge> : <Badge tone="ok">{t`All clear`}</Badge>}
      </div>
      <p className="max-w-3xl text-sm text-fg-muted">
        {t`Pick what to watch on your channels, videos and sites. Alerts appear in the bell and go to the e-mail addresses and Telegram chats you choose, with a message when they clear. Only this tenant's data is evaluated, every 30 seconds.`}
      </p>
      <ServiceStatusCard />
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="monitors">{t`Monitors`}</TabsTrigger>
          <TabsTrigger value="alerts">{t`Alert history`}{openCount ? <span className="ms-1 rounded-full bg-danger px-1.5 text-[10px] font-semibold text-white">{openCount}</span> : null}</TabsTrigger>
          <TabsTrigger value="destinations">{t`Destinations`}</TabsTrigger>
        </TabsList>
        <TabsContent value="monitors" className="pt-4"><MonitorsTab manage={manage} onDestinations={() => setTab('destinations')} /></TabsContent>
        <TabsContent value="alerts" className="pt-4"><AlertsTab /></TabsContent>
        <TabsContent value="destinations" className="pt-4"><DestinationsTab manage={manage} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------- service status ("תקינות השירות")

function ServiceStatusCard() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const q = useServiceStatus(tenant.slug);
  const names: Record<ServiceLayer['layer'], string> = { streaming: t`Streaming`, delivery: t`Delivery (CDN)`, origin: t`Origin`, qoe: t`Viewer experience (QoE)` };
  const words: Record<ServiceLevel, string> = { ok: t`Operational`, degraded: t`Degraded`, down: t`Outage`, unknown: t`Not measured` };
  const dot: Record<ServiceLevel, string> = { ok: 'bg-ok', degraded: 'bg-warn', down: 'bg-danger', unknown: 'bg-fg-muted/40' };
  if (q.isError || q.isPending) return null;
  return (
    <section aria-labelledby="svc-h" className="rounded-lg border border-border bg-surface p-3" data-testid="service-status">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 id="svc-h" className="font-semibold">{t`Service status`}</h2>
        <span className="text-xs text-fg-muted">{t`Your channels and hostnames, plus the shared ViewStream layers.`}</span>
      </div>
      <ul className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {q.data.layers.map((l) => {
          // streaming, delivery and origin are always watched by ViewStream's shared alerts (edges, origin, encoders);
          // only a layer nothing watches — no shared state, none of the tenant's monitors (QoE) — is "not monitored"
          // (grey), not a green "operational" (now14 demo retest P1-4; Dmitry 2026-10-14: CDN showed "not monitored")
          const platform = !!l.shared_level && l.shared_level !== 'unknown';
          const unmonitored = l.checks === 0 && !platform && l.level === 'ok';
          return (
          <li key={l.layer} className="flex items-start gap-2 rounded-md border border-border p-2" data-layer={l.layer} data-level={unmonitored ? 'unmonitored' : l.level}>
            <span className={cn('mt-1.5 size-3 shrink-0 rounded-full', unmonitored ? dot.unknown : dot[l.level])} aria-hidden />
            <div className="min-w-0">
              <div className="text-sm font-medium">{names[l.layer]}</div>
              <div className={cn('text-xs', l.level === 'down' ? 'text-danger' : l.level === 'degraded' ? 'text-warn' : 'text-fg-muted')}>
                {unmonitored ? t`Not monitored` : words[l.level]}
                {l.shared_level === 'degraded' || l.shared_level === 'down' ? <> · {t`ViewStream is on it`}</> : null}
                {l.firing ? <> · {t`${l.firing} of your checks firing`}</> : null}
              </div>
              {l.since && l.level !== 'ok' ? <div className="text-xs text-fg-muted">{t`since ${fmtDateTime(l.since, { locale, tz }, { timeStyle: 'short' })}`}</div> : null}
              {l.checks === 0 ? <div className="text-xs text-fg-muted">{platform ? t`watched by ViewStream · none of your own monitors` : t`no monitors on this layer`}</div> : null}
            </div>
          </li>
          );
        })}
      </ul>
      {q.data.ai?.length ? <AIServicesStatus items={q.data.ai} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------- shared bits

function useStatusBadge() {
  const { t } = useLingui();
  const m: Record<MonitorStatus, { tone: 'ok' | 'danger' | 'warn' | 'neutral' | 'primary'; label: string }> = {
    ok: { tone: 'ok', label: t`OK` }, firing: { tone: 'danger', label: t`Firing` }, pending: { tone: 'warn', label: t`Pending` },
    muted: { tone: 'neutral', label: t`Muted` }, nodata: { tone: 'neutral', label: t`No data` }, disabled: { tone: 'neutral', label: t`Off` },
  };
  return (s: string | undefined) => {
    const x = m[(s ?? 'nodata') as MonitorStatus] ?? m.nodata;
    return <Badge tone={x.tone} data-status={s}>{x.label}</Badge>;
  };
}

function useSeverityLabel() {
  const { t } = useLingui();
  return (s: string) => ({ critical: t`Critical`, warning: t`Warning`, info: t`Info` } as Record<string, string>)[s] ?? s;
}

function useTargetKindLabel() {
  const { t } = useLingui();
  return (k: MonitorTargetKind) => ({ channel: t`Channel`, asset: t`Video`, site: t`Site`, tenant: t`Whole tenant` })[k];
}

function checkLabel(defs: MonitorCheckDef[] | undefined, type: string, locale: 'he' | 'en') {
  const d = defs?.find((x) => x.type === type);
  return d ? (locale === 'he' ? d.label_he : d.label_en) : type;
}

const errLabels = (t: (s: TemplateStringsArray) => string) => ({ title: t`Could not load`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` });

// ---------------------------------------------------------------- monitors

function MonitorsTab({ manage, onDestinations }: { manage: boolean; onDestinations: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useMonitors(tenant.slug);
  const catalog = useCheckCatalog(tenant.slug);
  const [wizard, setWizard] = React.useState<Monitor | 'new' | null>(null);
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={errLabels(t)} />;
  if (q.isPending) return <Skeleton className="h-40" />;
  const ms = q.data.data;
  return (
    <div className="space-y-4">
      {manage ? (
        <div className="flex flex-wrap items-start gap-3">
          <Button onClick={() => setWizard('new')} data-testid="new-monitor"><Plus className="size-4" aria-hidden /> {t`New monitor`}</Button>
          <BasicPresetButton existing={ms} />
        </div>
      ) : <p className="text-sm text-fg-muted">{t`You can view monitors. Creating and changing them needs the Admin role (notifications).`}</p>}
      {ms.length === 0 ? (
        <EmptyState icon={<Activity className="size-8" aria-hidden />} title={t`No monitors yet`}
          description={t`Start with basic channel monitoring: feed down for 60 s, recording gaps, slow startup and playback errors.`} />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2" aria-label={t`Monitors`}>
          {ms.map((m) => <MonitorCard key={m.id} m={m} defs={catalog.data?.checks} manage={manage} onEdit={() => setWizard(m)} />)}
        </ul>
      )}
      {wizard ? <MonitorWizard initial={wizard === 'new' ? null : wizard} onClose={() => setWizard(null)} onDestinations={() => { setWizard(null); onDestinations(); }} /> : null}
    </div>
  );
}

/** One click: "ניטור בסיסי לערוץ" for a channel without one. */
function BasicPresetButton({ existing }: { existing: Monitor[] }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const channels = useChannels(tenant.slug);
  const preset = useBasicPreset(tenant.slug);
  const dests = useDestinations(tenant.slug);
  const covered = new Set(existing.filter((m) => m.preset === 'basic').map((m) => m.target_id));
  const options = (channels.data ?? []).map((c) => ({ value: c.id!, label: (c.title || c.slug || '') + (covered.has(c.id!) ? ` ✓` : ''), disabled: covered.has(c.id!) }));
  const [ch, setCh] = React.useState('');
  if (!options.length) return null;
  const active = (dests.data?.data ?? []).filter((d) => d.status === 'active').map((d) => d.id);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface p-2">
      <Zap className="size-4 text-primary" aria-hidden />
      <span className="text-sm font-medium">{t`Basic channel monitoring`}</span>
      <Select ariaLabel={t`Channel`} value={ch} onValueChange={setCh} options={options} placeholder={t`Pick a channel`} className="min-w-40" />
      <Button size="sm" variant="secondary" disabled={!ch} loading={preset.isPending} data-testid="basic-preset"
        onClick={() => preset.mutate({ channel_id: ch, destinations: active, name: t`Basic monitoring — ${(channels.data ?? []).find((c) => c.id === ch)?.title || (channels.data ?? []).find((c) => c.id === ch)?.slug || ''}` }, {
          onSuccess: () => { setCh(''); toast.push({ title: t`Basic monitoring is on`, description: t`Feed down 60 s, recorder gap, startup p95 > 5 s and error rate > 5 % for 10 min.`, tone: 'ok' }); },
          onError: (x) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' }),
        })}>
        {t`Turn on`}
      </Button>
    </div>
  );
}

function MonitorCard({ m, defs, manage, onEdit }: { m: Monitor; defs?: MonitorCheckDef[]; manage: boolean; onEdit: () => void }) {
  const { t } = useLingui();
  /* "0 plays (< 5)" / "3 attempts (< 10)" from the control plane → a sentence; any other reason as sent */
  const noDataReason = (raw: string | undefined, windowS: number | undefined): string | undefined => {
    if (!raw) return undefined;
    const mins = Math.max(1, Math.round((windowS || 600) / 60));
    const r = /^(\d+) (plays|attempts) \(< (\d+)\)$/.exec(raw.trim());
    if (!r) return raw;
    const n = Number(r[1]);
    const min = Number(r[3]);
    return r[2] === 'plays' ? t`${n} plays in the last ${mins} min (needs ${min})` : t`${n} play attempts in the last ${mins} min (needs ${min})`;
  };
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const status = useStatusBadge();
  const sev = useSeverityLabel();
  const kind = useTargetKindLabel();
  const patch = usePatchMonitor(tenant.slug);
  const del = useDeleteMonitor(tenant.slug);
  const snooze = useSnooze(tenant.slug);
  const unsnooze = useUnsnooze(tenant.slug);
  const [confirm, setConfirm] = React.useState(false);
  const fail = (x: unknown) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' });
  const snoozed = m.snoozed_until && new Date(m.snoozed_until).getTime() > Date.now();
  const target = m.target_label || m.target_host || (m.target_kind === 'tenant' ? t`Whole tenant` : m.target_id?.slice(0, 8));
  const stateOf = (key: string) => m.states?.find((s) => s.check_key === key);
  return (
    <li className={cn('space-y-3 rounded-lg border bg-surface p-3', m.status === 'firing' ? 'border-danger/50' : 'border-border')} data-testid="monitor-card" data-monitor={m.name}>
      <div className="flex flex-wrap items-center gap-2">
        {status(m.status)}
        <span className="min-w-0 flex-1 truncate text-start font-semibold" dir="auto">{m.name}</span>
        {manage ? <Switch checked={m.enabled} aria-label={t`Enabled`} onCheckedChange={(v) => patch.mutate({ id: m.id, enabled: v }, { onError: fail })} /> : null}
      </div>
      <div className="text-xs text-fg-muted">
        {kind(m.target_kind)}: {m.target_kind === 'channel' && m.target_id
          ? <Link to={`/t/${tenant.slug}/live/${m.target_id}` as never} className="text-primary hover:underline" dir="auto">{target}</Link>
          : <span className="ltr-isolate">{target}</span>}
        {m.quiet_hours ? <> · {t`Quiet hours`} <span className="ltr-isolate">{m.quiet_hours.from}–{m.quiet_hours.to}</span></> : null}
        {snoozed ? <> · {t`Muted until ${fmtDateTime(m.snoozed_until!, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' })}`}{m.snooze_reason ? ` (${m.snooze_reason})` : ''}</> : null}
      </div>
      <ul className="divide-y divide-border rounded-md border border-border text-sm">
        {m.checks.map((c) => {
          const s = stateOf(c.key ?? c.type);
          const d = defs?.find((x) => x.type === c.type);
          const noData = s?.detail && typeof s.detail['no_data'] === 'string' ? String(s.detail['no_data']) : undefined;
          // the reason a check has no data, shown in full (low traffic on our player is the usual one)
          const why = noDataReason(noData, c.window_s ?? d?.window_s);
          return (
            <li key={c.key ?? c.type} className="flex flex-wrap items-center gap-2 px-2 py-1.5" data-check={c.type} data-state={s?.state ?? 'nodata'}>
              <span className={cn('size-2 shrink-0 rounded-full', s?.state === 'firing' ? 'bg-danger' : s?.state === 'pending' ? 'bg-warn' : s?.state === 'ok' ? 'bg-ok' : 'bg-fg-muted/40')} aria-hidden />
              <span className="min-w-[8rem] flex-1 break-words">{checkLabel(defs, c.type, locale)}</span>
              {/* a long "no data" reason gets its own line under the check (it squeezed the label until the words overlapped) */}
              <span className={cn('min-w-0 text-xs text-fg-muted', why && (s?.value === undefined || s?.value === null) ? 'order-last basis-full ps-4' : '')} title={noData}>
                {c.type === 'feed_down'
                  ? (s?.value === 1 ? t`feed down` : s?.value === 0 ? t`feed up` : t`no data`)
                  : d?.unit === 'level' ? (s?.value !== undefined && s?.value !== null ? fmtCheckValue('level', s.value, locale) : t`no data`)
                  : <><bdi className="font-medium text-fg">{s?.value !== undefined && s?.value !== null ? fmtCheckValue(d?.unit, s.value, locale) : t`no data`}</bdi>
                    {' · '}{d?.op === 'lt' ? t`alert below` : t`alert above`} <bdi>{fmtCheckValue(d?.unit, c.threshold, locale)}</bdi></>}
                {why && (s?.value === undefined || s?.value === null) ? <> · <span data-testid="nodata-reason">{why}</span></> : null}
              </span>
              {/* the severity is what an alert WOULD be — plain text, so only the dot (the state) carries colour (Dmitry
                  2026-10-05: "its green or critical? its in the same line") */}
              <span className="text-xs text-fg-muted" data-testid="check-severity">{t`if it fires: ${sev(c.severity ?? 'warning')}`}</span>
            </li>
          );
        })}
      </ul>
      {manage ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={onEdit}><Pencil className="size-4" aria-hidden /> {t`Edit`}</Button>
          {snoozed ? (
            <Button size="sm" variant="secondary" loading={unsnooze.isPending} onClick={() => unsnooze.mutate(m.id, { onError: fail })}><Play className="size-4" aria-hidden /> {t`End mute`}</Button>
          ) : (
            <Select ariaLabel={t`Mute`} value="" placeholder={t`Mute / maintenance…`} className="w-48"
              options={[{ value: '60', label: t`1 hour` }, { value: '240', label: t`4 hours` }, { value: '1440', label: t`24 hours` }, { value: '10080', label: t`7 days` }]}
              onValueChange={(v) => snooze.mutate({ id: m.id, minutes: Number(v), reason: t`maintenance` }, { onError: fail })} />
          )}
          <Button size="sm" variant="ghost" aria-label={t`Delete monitor ${m.name}`} onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /></Button>
        </div>
      ) : null}
      {confirm ? (
        <ConfirmByNameDialog open onOpenChange={setConfirm} name={m.name} title={t`Delete monitor`} description={t`Type the monitor name to confirm. Its alert history stays.`}
          labels={{ field: t`Monitor name`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending}
          onConfirm={() => del.mutateAsync(m.id).then(() => setConfirm(false))} />
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------- create / edit wizard

type Step = 1 | 2 | 3 | 4;

function MonitorWizard({ initial, onClose, onDestinations }: { initial: Monitor | null; onClose: () => void; onDestinations: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const catalog = useCheckCatalog(tenant.slug);
  const channels = useChannels(tenant.slug);
  const dests = useDestinations(tenant.slug);
  const create = useCreateMonitor(tenant.slug);
  const patch = usePatchMonitor(tenant.slug);
  const kindLabel = useTargetKindLabel();
  const sev = useSeverityLabel();
  const [step, setStep] = React.useState<Step>(initial ? 2 : 1);
  const [kind, setKind] = React.useState<MonitorTargetKind>(initial?.target_kind ?? 'channel');
  const [targetId, setTargetId] = React.useState(initial?.target_id ?? '');
  const [host, setHost] = React.useState(initial?.target_host ?? '');
  const [checks, setChecks] = React.useState<MonitorCheck[]>(initial?.checks ?? []);
  const [destIds, setDestIds] = React.useState<string[]>(initial?.destinations ?? []);
  const [inapp, setInapp] = React.useState(initial?.inapp ?? true);
  const [resolve, setResolve] = React.useState(initial?.notify_resolve ?? true);
  const [quiet, setQuiet] = React.useState(!!initial?.quiet_hours);
  const [qFrom, setQFrom] = React.useState(initial?.quiet_hours?.from ?? '23:00');
  const [qTo, setQTo] = React.useState(initial?.quiet_hours?.to ?? '07:00');
  const [qCrit, setQCrit] = React.useState(initial?.quiet_hours?.allow_critical ?? true);
  const [name, setName] = React.useState(initial?.name ?? '');
  const [err, setErr] = React.useState<string>();
  const defs = (catalog.data?.checks ?? []).filter((d) => d.targets.includes(kind));
  const families: { id: MonitorCheckDef['family']; label: string }[] = [
    { id: 'live', label: t`Live stream` }, { id: 'qoe', label: t`Quality of experience (QoE)` }, { id: 'delivery', label: t`Delivery` },
    { id: 'origin', label: t`Origin` }, { id: 'service', label: t`Service status` },
  ];
  const chOptions = (channels.data ?? []).map((c) => ({ value: c.id!, label: c.title || c.slug || '' }));
  const targetOk = kind === 'tenant' || (kind === 'site' ? /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host.trim()) : !!targetId);
  const toggleCheck = (d: MonitorCheckDef, on: boolean) => setChecks((cs) => on
    ? [...cs, { key: d.type, type: d.type, threshold: d.threshold, for_s: d.for_s, window_s: d.window_s || undefined, severity: d.severity }]
    : cs.filter((c) => c.type !== d.type));
  const setCheck = (type: string, p: Partial<MonitorCheck>) => setChecks((cs) => cs.map((c) => (c.type === type ? { ...c, ...p } : c)));
  const pickKind = (k: MonitorTargetKind) => { setKind(k); setChecks([]); setTargetId(''); };
  const defaultName = () => {
    const label = kind === 'channel' ? chOptions.find((o) => o.value === targetId)?.label : kind === 'site' ? host : kind === 'asset' ? targetId.slice(0, 8) : t`Whole tenant`;
    return `${kindLabel(kind)} ${label ?? ''}`.trim();
  };
  const save = () => {
    setErr(undefined);
    const body = {
      name: name.trim() || defaultName(), target_kind: kind, target_id: kind === 'channel' || kind === 'asset' ? targetId : undefined,
      target_host: kind === 'site' ? host.trim().toLowerCase() : undefined, checks, destinations: destIds, inapp, notify_resolve: resolve,
      quiet_hours: quiet ? { from: qFrom, to: qTo, tz: 'Asia/Jerusalem', allow_critical: qCrit } : null,
    };
    const done = { onSuccess: onClose, onError: (x: unknown) => setErr(problemText(x)) };
    if (initial) patch.mutate({ id: initial.id, ...body }, done);
    else create.mutate(body, done);
  };
  const stepTitle = { 1: t`1 · What to watch`, 2: t`2 · Checks and thresholds`, 3: t`3 · Where to send`, 4: t`4 · Name and save` }[step];
  const activeDests = (dests.data?.data ?? []).filter((d) => d.status !== 'disabled');
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent title={initial ? t`Edit monitor` : t`New monitor`} closeLabel={t`Close`} className="max-w-2xl" data-testid="monitor-wizard">
        <ol className="mb-4 flex gap-1 text-xs" aria-label={t`Steps`}>
          {[1, 2, 3, 4].map((s) => <li key={s} className={cn('h-1.5 flex-1 rounded-full', s <= step ? 'bg-primary' : 'bg-surface-2')} aria-current={s === step ? 'step' : undefined} />)}
        </ol>
        <h2 className="mb-3 font-semibold">{stepTitle}</h2>
        {step === 1 ? (
          <div className="space-y-4">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{t`Target`}</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup">
                {(['channel', 'site', 'asset', 'tenant'] as MonitorTargetKind[]).map((k) => (
                  <label key={k} className={cn('flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm', kind === k ? 'border-primary bg-primary/5' : 'border-border')}>
                    <input type="radio" name="kind" checked={kind === k} onChange={() => pickKind(k)} /> {kindLabel(k)}
                  </label>
                ))}
              </div>
            </fieldset>
            {kind === 'channel' ? (
              <Field id="mon-channel" label={t`Channel`}>
                <Select ariaLabel={t`Channel`} value={targetId} onValueChange={setTargetId} options={chOptions} placeholder={t`Pick a channel`} />
              </Field>
            ) : kind === 'site' ? (
              <Field id="mon-host" label={t`Site host`} hint={t`The host name the player runs on, e.g. tv10poc.viewstream.co.il`}>
                <Input dir="ltr" value={host} onChange={(e) => setHost(e.target.value)} placeholder="www.example.co.il" />
              </Field>
            ) : kind === 'asset' ? (
              <Field id="mon-asset" label={t`Video id`} hint={t`Copy it from the video page in the library.`}>
                <Input dir="ltr" value={targetId} onChange={(e) => setTargetId(e.target.value.trim())} placeholder="0192…" />
              </Field>
            ) : <p className="text-sm text-fg-muted">{t`Quality checks over all your plays, and subtitle/translation delivery.`}</p>}
          </div>
        ) : null}
        {step === 2 ? (
          <div className="space-y-4">
            {families.map((f) => {
              const list = defs.filter((d) => d.family === f.id);
              if (!list.length) return null;
              return (
                <fieldset key={f.id} className="space-y-2">
                  <legend className="text-sm font-semibold">{f.label}</legend>
                  {list.map((d) => {
                    const c = checks.find((x) => x.type === d.type);
                    return (
                      <div key={d.type} className={cn('rounded-md border p-2', c ? 'border-primary/50 bg-primary/5' : 'border-border')} data-check-def={d.type}>
                        <label className="flex items-start gap-2 text-sm" htmlFor={`chk-${d.type}`}>
                          <Checkbox id={`chk-${d.type}`} checked={!!c} onCheckedChange={(v) => toggleCheck(d, v === true)} className="mt-0.5" />
                          <span><span className="font-medium">{locale === 'he' ? d.label_he : d.label_en}</span>
                            <span className="block text-xs text-fg-muted">{locale === 'he' ? d.help_he : d.help_en}</span></span>
                        </label>
                        {c ? <CheckEditor d={d} c={c} onChange={(p) => setCheck(d.type, p)} sevLabel={sev} kind={kind} /> : null}
                      </div>
                    );
                  })}
                </fieldset>
              );
            })}
          </div>
        ) : null}
        {step === 3 ? (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={inapp} onCheckedChange={setInapp} /> {t`Show in the bell (Studio)`}</label>
            <fieldset>
              <legend className="mb-1 text-sm font-medium">{t`E-mail and Telegram`}</legend>
              {activeDests.length === 0 ? (
                <p className="text-sm text-fg-muted">{t`No destinations yet.`} <button type="button" className="text-primary underline" onClick={onDestinations}>{t`Add an e-mail or connect Telegram`}</button></p>
              ) : (
                <div className="space-y-1">
                  {activeDests.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={destIds.includes(d.id)} onCheckedChange={(v) => setDestIds((xs) => (v === true ? [...xs, d.id] : xs.filter((x) => x !== d.id)))} />
                      {d.kind === 'email' ? <Mail className="size-4" aria-hidden /> : <MessageCircle className="size-4" aria-hidden />}
                      <span dir="auto">{d.name}</span>{d.status === 'pending' ? <Badge tone="warn">{t`awaiting confirmation`}</Badge> : null}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
            <label className="flex items-center gap-2 text-sm"><Switch checked={resolve} onCheckedChange={setResolve} /> {t`Send a message when the alert clears`}</label>
            <div className="space-y-2 rounded-md border border-border p-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={quiet} onCheckedChange={setQuiet} /> {t`Quiet hours (no e-mail or Telegram)`}</label>
              {quiet ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Field id="q-from" label={t`From`}><Input type="time" dir="ltr" value={qFrom} onChange={(e) => setQFrom(e.target.value)} className="w-28" /></Field>
                  <Field id="q-to" label={t`To`}><Input type="time" dir="ltr" value={qTo} onChange={(e) => setQTo(e.target.value)} className="w-28" /></Field>
                  <label className="flex items-center gap-2 pb-2 text-sm"><Checkbox checked={qCrit} onCheckedChange={(v) => setQCrit(v === true)} /> {t`Critical alerts still go out`}</label>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {step === 4 ? (
          <div className="space-y-3">
            <Field id="mon-name" label={t`Monitor name`} error={err}>
              <Input maxLength={120} value={name} placeholder={defaultName()} onChange={(e) => setName(e.target.value)} />
            </Field>
            <p className="text-sm text-fg-muted">{t`${checks.length} checks · ${destIds.length} destinations`}</p>
          </div>
        ) : null}
        {err && step !== 4 ? <p className="mt-3 text-sm text-danger" role="alert">{err}</p> : null}
        <div className="mt-6 flex justify-between gap-2">
          <Button variant="secondary" disabled={step === 1 || (!!initial && step === 2)} onClick={() => setStep((s) => (s - 1) as Step)}>{t`Back`}</Button>
          {step < 4 ? (
            <Button disabled={(step === 1 && !targetOk) || (step === 2 && checks.length === 0)} onClick={() => setStep((s) => (s + 1) as Step)} data-testid="wizard-next">{t`Next`}</Button>
          ) : (
            <Button loading={create.isPending || patch.isPending} onClick={save} data-testid="wizard-save">{initial ? t`Save` : t`Create monitor`}</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CheckEditor({ d, c, onChange, sevLabel, kind }: { d: MonitorCheckDef; c: MonitorCheck; onChange: (p: Partial<MonitorCheck>) => void; sevLabel: (s: string) => string; kind: MonitorTargetKind }) {
  const { t } = useLingui();
  const unit = { s: t`seconds`, ms: t`ms`, pct: '%', count: d.type === 'plays' ? t`plays` : '', min: t`minutes`, luma: t`brightness 0–255`, days: t`days`, level: '' }[d.unit];
  const showThreshold = d.type !== 'feed_down' && d.unit !== 'level';
  const { names: aiNames } = useAIServiceTexts();
  const lipsyncOn = useLipsyncAvailable(); // LIPSYNC_ENABLED=false: no lip-sync service to pick
  const hostParam = ['edge_5xx_rate', 'edge_4xx_rate', 'segment_time_p95', 'cache_hit_ratio', 'origin_fetch_p95', 'edge_reachable', 'tls_expiry'].includes(d.type);
  return (
    <div className="mt-2 flex flex-wrap items-end gap-3 ps-6 text-sm">
      {showThreshold ? (
        <Field id={`th-${d.type}`} label={d.op === 'lt' ? t`Alert below` : t`Alert above`}>
          <div className="flex items-center gap-1">
            <Input type="number" dir="ltr" className="w-28" min={0} step="any" value={c.threshold} onChange={(e) => onChange({ threshold: Number(e.target.value) })} />
            <span className="text-xs text-fg-muted">{unit}</span>
          </div>
        </Field>
      ) : null}
      <Field id={`for-${d.type}`} label={t`For (minutes)`}>
        <Input type="number" dir="ltr" className="w-24" min={0} step="0.5" value={c.for_s / 60} onChange={(e) => onChange({ for_s: Math.round(Number(e.target.value) * 60) })} />
      </Field>
      {d.window_s ? (
        <Field id={`win-${d.type}`} label={t`Window (minutes)`}>
          <Input type="number" dir="ltr" className="w-24" min={1} max={360} value={(c.window_s ?? d.window_s) / 60} onChange={(e) => onChange({ window_s: Math.round(Number(e.target.value)) * 60 })} />
        </Field>
      ) : null}
      <Field id={`sev-${d.type}`} label={t`Severity`}>
        <Select ariaLabel={t`Severity`} value={c.severity ?? d.severity} onValueChange={(v) => onChange({ severity: v as MonitorCheck['severity'] })}
          options={['info', 'warning', 'critical'].map((s) => ({ value: s, label: sevLabel(s) }))} className="w-32" />
      </Field>
      {d.unit === 'level' ? (
        <>
          <Field id={`lvl-${d.type}`} label={t`Alert when`}>
            <Select ariaLabel={t`Alert when`} value={String(c.threshold)} onValueChange={(v) => onChange({ threshold: Number(v) })} className="w-44"
              options={[{ value: '0', label: t`degraded or down` }, { value: '1', label: t`down only` }]} />
          </Field>
          {d.type === 'service_status' ? (
            <Field id={`layer-${d.type}`} label={t`Layer`}>
              <Select ariaLabel={t`Layer`} value={c.params?.layer ?? 'any'} onValueChange={(v) => onChange({ params: { ...c.params, layer: v as 'any' } })} className="w-36"
                options={[{ value: 'any', label: t`Any` }, { value: 'streaming', label: t`Streaming` }, { value: 'delivery', label: t`Delivery (CDN)` }, { value: 'origin', label: t`Origin` }]} />
            </Field>
          ) : null}
          {d.type === 'ai_services' ? (
            <Field id={`aisvc-${d.type}`} label={t`AI service`}>
              <Select ariaLabel={t`AI service`} value={c.params?.service ?? 'any'} onValueChange={(v) => onChange({ params: { ...c.params, service: v as 'any' } })} className="w-56"
                options={[{ value: 'any', label: t`Any` }, ...(['subtitles', 'translation', 'upscale', 'lipsync'] as const).filter((v) => v !== 'lipsync' || lipsyncOn !== false).map((v) => ({ value: v, label: aiNames[v] }))]} />
            </Field>
          ) : null}
        </>
      ) : null}
      {hostParam ? (
        <Field id={`host-${d.type}`} label={t`CDN host (optional)`}>
          <Input dir="ltr" className="w-60" placeholder={t`all your hostnames`} value={c.params?.host ?? ''}
            onChange={(e) => { const h = e.target.value.trim().toLowerCase(); const { host: _h, ...rest } = c.params ?? {}; void _h; onChange({ params: h ? { ...rest, host: h } : rest }); }} />
        </Field>
      ) : null}
      {d.type === 'ingest_feed_down' ? (
        <Field id={`feed-${d.type}`} label={t`Feed`}>
          <Select ariaLabel={t`Feed`} value={c.params?.feed ?? 'a'} onValueChange={(v) => onChange({ params: { ...c.params, feed: v as 'a' | 'b' } })} className="w-40"
            options={[{ value: 'a', label: t`A (primary)` }, { value: 'b', label: t`B (backup)` }]} />
        </Field>
      ) : null}
      {d.type === 'recording_leg_down' ? (
        <Field id={`leg-${d.type}`} label={t`Recorder`}>
          <Select ariaLabel={t`Recorder`} value={String(c.params?.leg ?? 'any')} className="w-56"
            onValueChange={(v) => { const { leg: _l, ...rest } = c.params ?? {}; void _l; onChange({ params: v === 'any' ? rest : { ...rest, leg: Number(v) as 1 | 2 } }); }}
            options={[{ value: 'any', label: t`Either recorder (the slower one)` }, { value: '1', label: t`Recorder 1` }, { value: '2', label: t`Recorder 2` }]} />
        </Field>
      ) : null}
      {d.type === 'plays' && kind === 'channel' ? (
        <label className="flex items-center gap-2 pb-2"><Checkbox checked={!!c.params?.during_programme} onCheckedChange={(v) => onChange({ params: { ...c.params, during_programme: v === true } })} /> {t`Only while a programme is on air`}</label>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- alert history

function AlertsTab() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const [state, setState] = React.useState<'all' | 'firing' | 'resolved'>('all');
  const q = useAlerts(tenant.slug, state === 'all' ? undefined : state);
  const canAck = can(scopes, 'notifications:manage') || can(scopes, 'channels:operate');
  return (
    <div className="space-y-3">
      <div className="flex gap-2" role="group" aria-label={t`Filter`}>
        {([['all', t`All`], ['firing', t`Firing`], ['resolved', t`Resolved`]] as const).map(([v, l]) => (
          <Button key={v} size="sm" variant={state === v ? 'primary' : 'secondary'} aria-pressed={state === v} onClick={() => setState(v)}>{l}</Button>
        ))}
      </div>
      {q.isError ? <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={errLabels(t)} />
        : q.isPending ? <Skeleton className="h-40" />
        : q.data.length === 0 ? <EmptyState icon={<ShieldCheck className="size-8" aria-hidden />} title={t`No alerts`} description={t`Alerts of your monitors appear here with their deliveries.`} />
        : <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`Alert history`}>{q.data.map((a) => <AlertRow key={a.id} a={a} canAck={canAck} />)}</ul>}
    </div>
  );
}

function dur(ms: number, locale: 'he' | 'en') {
  const m = Math.round(ms / 60000);
  if (m < 1) return locale === 'he' ? 'פחות מדקה' : '< 1 min';
  if (m < 60) return locale === 'he' ? `${m} דק׳` : `${m} min`;
  const h = Math.floor(m / 60);
  return locale === 'he' ? `${h} שע׳ ${m % 60} דק׳` : `${h} h ${m % 60} min`;
}

function AlertRow({ a, canAck }: { a: MonitorAlert; canAck: boolean }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const sev = useSeverityLabel();
  const ack = useAckAlert(tenant.slug);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const title = locale === 'he' ? a.title_he : a.title_en;
  const body = locale === 'he' ? a.body_he : a.body_en;
  const end = a.resolved_at ? new Date(a.resolved_at).getTime() : Date.now();
  const sent = (a.sends ?? []).filter((s) => s.status === 'sent').length;
  const skipped = (a.sends ?? []).filter((s) => s.status !== 'sent');
  const href = a.link ? new URL(a.link).pathname : undefined;
  return (
    <li className={cn('space-y-1 p-3', a.resolved_at ? '' : 'bg-danger/5')} data-testid="alert-row" data-state={a.resolved_at ? 'resolved' : 'firing'}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={a.resolved_at ? 'ok' : a.severity === 'critical' ? 'danger' : 'warn'}>{a.resolved_at ? t`Resolved` : sev(a.severity)}</Badge>
        {a.muted ? <Badge><BellOff className="size-3" aria-hidden /> {a.muted === 'snoozed' ? t`muted` : t`quiet hours`}</Badge> : null}
        {a.acked_at ? <Badge tone="primary"><CheckCircle2 className="size-3" aria-hidden /> {t`Acknowledged`}</Badge> : null}
        <span className="min-w-0 flex-1 font-medium">{href ? <Link to={href as never} className="hover:underline">{title}</Link> : title}</span>
      </div>
      {body ? <p className="text-xs text-fg-muted">{body}</p> : null}
      <p className="text-xs text-fg-muted">
        {fmtDateTime(a.started_at, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' })} · {dur(end - new Date(a.started_at).getTime(), locale)} · {a.monitor_name}
        {sent ? <> · {t`${sent} sent`}</> : null}
        {skipped.length ? <> · <span title={skipped.map((s) => `${s.kind} ${s.event}: ${s.status} ${s.reason ?? ''}`).join('\n')}>{t`${skipped.length} not sent`}</span></> : null}
        {a.ack_note ? <> · “{a.ack_note}”</> : null}
      </p>
      {canAck && !a.acked_at ? (
        noteOpen ? (
          <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); ack.mutate({ id: a.id, note }, { onSuccess: () => setNoteOpen(false), onError: (x) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' }) }); }}>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t`Note (optional)`} className="max-w-sm" maxLength={500} />
            <Button type="submit" size="sm" loading={ack.isPending}>{t`Acknowledge`}</Button>
          </form>
        ) : <Button size="sm" variant="secondary" onClick={() => setNoteOpen(true)} data-testid="ack">{t`Acknowledge`}</Button>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------- destinations

function DestinationsTab({ manage }: { manage: boolean }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useDestinations(tenant.slug);
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={errLabels(t)} />;
  if (q.isPending) return <Skeleton className="h-40" />;
  return (
    <div className="space-y-6">
      {manage ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <EmailForm />
          <TelegramConnect configured={q.data.telegram.configured} bot={q.data.telegram.bot} />
        </div>
      ) : null}
      {q.data.data.length === 0 ? <EmptyState icon={<BellRing className="size-8" aria-hidden />} title={t`No destinations yet`} description={t`Alerts still appear in the bell.`} />
        : <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`Destinations`}>{q.data.data.map((d) => <DestinationRow key={d.id} d={d} manage={manage} />)}</ul>}
    </div>
  );
}

function EmailForm() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const create = useCreateDestination(tenant.slug);
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [lang, setLang] = React.useState<'he' | 'en'>(locale);
  const [err, setErr] = React.useState<string>();
  return (
    <form className="space-y-3 rounded-lg border border-border bg-surface p-3" onSubmit={(e) => {
      e.preventDefault();
      setErr(undefined);
      create.mutate({ target: email.trim(), name: name.trim() || undefined, lang }, {
        onSuccess: (d) => {
          setEmail(''); setName('');
          toast.push(d.status === 'pending'
            ? { title: t`Confirmation sent`, description: t`The address receives alerts after its owner confirms the link we mailed (72 hours).`, tone: 'ok' }
            : { title: t`E-mail added`, tone: 'ok' });
        },
        onError: (x) => setErr(problemText(x)),
      });
    }}>
      <h2 className="flex items-center gap-2 font-semibold"><Mail className="size-4" aria-hidden /> {t`E-mail`}</h2>
      <p className="text-xs text-fg-muted">{t`Members of this tenant are added at once; any other address must confirm first (double opt-in).`}</p>
      <Field id="dest-email" label={t`Address`} error={err}>
        <Input type="email" dir="ltr" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="noc@example.co.il" />
      </Field>
      <div className="flex flex-wrap items-end gap-2">
        <Field id="dest-name" label={t`Name (optional)`}><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="w-56" /></Field>
        <Field id="dest-lang" label={t`Language`}>
          <Select ariaLabel={t`Language`} value={lang} onValueChange={(v) => setLang(v as 'he' | 'en')} options={[{ value: 'he', label: 'עברית' }, { value: 'en', label: 'English' }]} className="w-32" />
        </Field>
        <Button type="submit" loading={create.isPending} disabled={!email.includes('@')}><Plus className="size-4" aria-hidden /> {t`Add`}</Button>
      </div>
    </form>
  );
}

function TelegramConnect({ configured, bot }: { configured: boolean; bot?: string }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  const link = useTelegramLink(tenant.slug);
  const [res, setRes] = React.useState<{ url: string; group_url: string; expires_at: string }>();
  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
      <h2 className="flex items-center gap-2 font-semibold"><MessageCircle className="size-4" aria-hidden /> {t`Telegram`}</h2>
      <p className="text-xs text-fg-muted">{t`Alerts go to your own chat or group through the ViewStream bot${bot ? ` (@${bot})` : ''}. The link works once, for 15 minutes.`}</p>
      {configured ? (
        <Button variant="secondary" loading={link.isPending} data-testid="telegram-connect"
          onClick={() => link.mutate(locale, { onSuccess: setRes, onError: (x) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' }) })}>
          <MessageCircle className="size-4" aria-hidden /> {t`Connect Telegram`}
        </Button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" disabled data-testid="telegram-connect"><MessageCircle className="size-4" aria-hidden /> {t`Connect Telegram`}</Button>
          <Badge tone="warn">{t`The ViewStream bot is not set up yet`}</Badge>
        </div>
      )}
      {res ? (
        <Dialog open onOpenChange={(o) => { if (!o) setRes(undefined); }}>
          <DialogContent title={t`Connect Telegram`} closeLabel={t`Close`}>
            <ol className="list-decimal space-y-2 ps-5 text-sm">
              <li>{t`Open the link on a device with Telegram and press Start.`}</li>
              <li>{t`For a group: use the group link and pick the group — the bot joins and links it.`}</li>
              <li>{t`The chat appears under Destinations; pick it in your monitors.`}</li>
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              <a className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-fg" href={res.url} target="_blank" rel="noreferrer"><Send className="size-4" aria-hidden /> {t`Open in Telegram`}</a>
              <a className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-4 text-sm" href={res.group_url} target="_blank" rel="noreferrer">{t`Add to a group`}</a>
            </div>
            <p className="mt-3 text-xs text-fg-muted">{t`Valid until ${fmtDateTime(res.expires_at, { locale, tz }, { timeStyle: 'short' })}`}</p>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function DestinationRow({ d, manage }: { d: AlertDestination; manage: boolean }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const toast = useToast();
  const patch = usePatchDestination(tenant.slug);
  const del = useDeleteDestination(tenant.slug);
  const test = useTestDestination(tenant.slug);
  const resend = useResendDestination(tenant.slug);
  const [confirm, setConfirm] = React.useState(false);
  const fail = (x: unknown) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' });
  const st = { active: { tone: 'ok' as const, label: t`Active` }, pending: { tone: 'warn' as const, label: t`Awaiting confirmation` }, disabled: { tone: 'neutral' as const, label: t`Off` } }[d.status];
  return (
    <li className="flex flex-wrap items-center gap-3 p-3" data-testid="destination-row" data-kind={d.kind}>
      {d.kind === 'email' ? <Mail className="size-4 text-fg-muted" aria-hidden /> : <MessageCircle className="size-4 text-fg-muted" aria-hidden />}
      <div className="min-w-[12rem] flex-1">
        <span className="block font-medium" dir="auto">{d.name}</span>
        <span className="text-xs text-fg-muted"><span className="ltr-isolate">{d.target}</span> · {d.lang === 'he' ? 'עברית' : 'English'}{d.disabled_reason ? ` · ${d.disabled_reason}` : ''}</span>
      </div>
      <Badge tone={st.tone}>{st.label}</Badge>
      {manage ? (
        <>
          {d.status === 'pending' ? <Button size="sm" variant="secondary" loading={resend.isPending} onClick={() => resend.mutate(d.id, { onSuccess: () => toast.push({ title: t`Confirmation sent again`, tone: 'ok' }), onError: fail })}>{t`Resend`}</Button> : null}
          {d.status === 'active' ? (
            <Button size="sm" variant="secondary" loading={test.isPending}
              onClick={() => test.mutate(d.id, { onSuccess: (r) => toast.push(r.status === 'sent' ? { title: t`Test sent`, tone: 'ok' } : { title: t`Test failed`, description: r.error, tone: 'danger' }), onError: fail })}>
              <Send className="size-4" aria-hidden /> {t`Send test`}
            </Button>
          ) : null}
          {d.status !== 'pending' ? (
            <Button size="sm" variant="ghost" aria-label={d.status === 'active' ? t`Turn off` : t`Turn on`}
              onClick={() => patch.mutate({ id: d.id, status: d.status === 'active' ? 'disabled' : 'active' }, { onError: fail })}>
              {d.status === 'active' ? <Pause className="size-4" aria-hidden /> : <Play className="size-4" aria-hidden />}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" aria-label={t`Delete destination ${d.name}`} onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /></Button>
        </>
      ) : null}
      {confirm ? (
        <ConfirmByNameDialog open onOpenChange={setConfirm} name={d.name} title={t`Delete destination`} description={t`Type the name to confirm. It is removed from every monitor.`}
          labels={{ field: t`Name`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending}
          onConfirm={() => del.mutateAsync(d.id).then(() => setConfirm(false))} />
      ) : null}
    </li>
  );
}
